/**
 * Customer planning preference ("planvenster") on a project — frontend mirror of
 * smartflow-api/utils/planningWindow.js. Keep the two in sync.
 *
 * {
 *   mode: 'any' | 'prefer' | 'only',
 *   weekdays: number[]      // ISO weekday 1 (Mon) … 7 (Sun)
 *   dayparts: string[]      // 'morning' | 'afternoon'
 *   not_before: 'YYYY-MM-DD' | null
 *   not_after:  'YYYY-MM-DD' | null
 *   note: string
 * }
 */

export const WINDOW_MODES = [
  { value: 'any', label: 'Maakt niet uit' },
  { value: 'prefer', label: 'Voorkeur' },
  { value: 'only', label: 'Alleen' },
];

export const WEEKDAY_OPTIONS = [
  { value: 1, label: 'ma' },
  { value: 2, label: 'di' },
  { value: 3, label: 'wo' },
  { value: 4, label: 'do' },
  { value: 5, label: 'vr' },
  { value: 6, label: 'za' },
  { value: 7, label: 'zo' },
];

export const DAYPART_OPTIONS = [
  { value: 'morning', label: 'Ochtend' },
  { value: 'afternoon', label: 'Middag' },
];

const WEEKDAY_SHORT = { 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za', 7: 'zo' };
const DAYPART_LABEL = { morning: 'ochtend', afternoon: 'middag' };
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const AFTERNOON_START_MINUTES = 12 * 60;

function isDateString(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function emptyPlanningWindow() {
  return { mode: 'any', weekdays: [], dayparts: [], not_before: null, not_after: null, note: '' };
}

/** True when the window carries any constraint or note. */
export function hasPlanningWindow(window) {
  if (!window) return false;
  const mode = window.mode || 'any';
  const hasDays = (window.weekdays || []).length > 0 || (window.dayparts || []).length > 0;
  return (
    (mode !== 'any' && hasDays) ||
    Boolean(window.not_before) ||
    Boolean(window.not_after) ||
    Boolean(window.note)
  );
}

/** ISO weekday (1 = Monday) of a YYYY-MM-DD string, calendar-only. */
export function isoWeekday(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 ? 7 : day;
}

export function formatWeekdays(weekdays) {
  return (weekdays || [])
    .map((d) => WEEKDAY_SHORT[d])
    .filter(Boolean)
    .join('/');
}

export function formatShortDate(dateStr) {
  if (!isDateString(dateStr)) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
}

/**
 * Evaluate a date (and optionally start time in minutes) against a window.
 * @returns {{ match: 'inside'|'outside'|'neutral', hard: boolean, reason: string|null }}
 */
export function evaluatePlanningWindow(window, dateStr, startMinutes = null) {
  if (!window || !isDateString(dateStr)) return { match: 'neutral', hard: false, reason: null };

  if (window.not_before && dateStr < window.not_before) {
    return {
      match: 'outside',
      hard: true,
      reason: `niet voor ${formatShortDate(window.not_before)}`,
    };
  }
  if (window.not_after && dateStr > window.not_after) {
    return { match: 'outside', hard: true, reason: `niet na ${formatShortDate(window.not_after)}` };
  }

  const mode = window.mode || 'any';
  if (mode === 'any') {
    const bounded = Boolean(window.not_before || window.not_after);
    return { match: bounded ? 'inside' : 'neutral', hard: false, reason: null };
  }

  const hard = mode === 'only';
  const weekdays = Array.isArray(window.weekdays) ? window.weekdays : [];
  const dayparts = Array.isArray(window.dayparts) ? window.dayparts : [];

  if (weekdays.length > 0 && !weekdays.includes(isoWeekday(dateStr))) {
    return {
      match: 'outside',
      hard,
      reason: `${hard ? 'alleen' : 'liefst'} ${formatWeekdays(weekdays)}`,
    };
  }

  if (dayparts.length > 0 && startMinutes !== null && startMinutes !== undefined) {
    const part = startMinutes < AFTERNOON_START_MINUTES ? 'morning' : 'afternoon';
    if (!dayparts.includes(part)) {
      return {
        match: 'outside',
        hard,
        reason: `${hard ? 'alleen' : 'liefst'} ${dayparts.map((p) => DAYPART_LABEL[p]).join('/')}`,
      };
    }
  }

  return { match: 'inside', hard, reason: null };
}

/**
 * Compact label: "alleen vr", "liefst di/wo · ochtend", "vanaf 14 okt". Null if nothing.
 */
export function formatPlanningWindow(window) {
  if (!window) return null;
  const parts = [];
  const mode = window.mode || 'any';
  const weekdays = Array.isArray(window.weekdays) ? window.weekdays : [];
  const dayparts = Array.isArray(window.dayparts) ? window.dayparts : [];

  if (mode !== 'any') {
    const prefix = mode === 'only' ? 'alleen' : 'liefst';
    const bits = [];
    if (weekdays.length) bits.push(formatWeekdays(weekdays));
    if (dayparts.length) bits.push(dayparts.map((p) => DAYPART_LABEL[p]).join('/'));
    if (bits.length) parts.push(`${prefix} ${bits.join(' · ')}`);
  }
  if (window.not_before) parts.push(`vanaf ${formatShortDate(window.not_before)}`);
  if (window.not_after) parts.push(`t/m ${formatShortDate(window.not_after)}`);

  return parts.length ? parts.join(' · ') : null;
}

/** Window of a work order (inherited from its project). */
export function getWorkOrderWindow(workOrder) {
  return workOrder?.project?.planning_window || null;
}
